/**
 * Utility functions for detecting where search matches occurred in thread events
 * and determining what action should be taken
 */

export interface SearchMatchMetadata {
  eventType: string
  subject: string | null
  body: string | null
  summary: string | null
  transcript: string | null
}

/**
 * Types of deeplink actions that can be triggered based on search context
 */
export enum DeeplinkActionType {
  /**
   * Default: Just scroll to the matching event
   */
  SCROLL = 'SCROLL',

  /**
   * Scroll to event AND open detail drawer (e.g., email drawer for quotedText matches)
   */
  SCROLL_AND_DRAWER = 'SCROLL_AND_DRAWER',
}

/**
 * Determines if a search query matched in primary searchable fields
 * (subject, body, summary, transcript) vs secondary fields (quotedText, sender)
 *
 * @param query - The search query string
 * @param metadata - Event metadata containing searchable fields
 * @returns true if match is in primary fields, false if only in secondary fields
 */
export function isMatchInPrimaryFields(
  query: string,
  metadata: SearchMatchMetadata,
): boolean {
  const queryLower = query.toLowerCase()

  const matchInSubject = metadata.subject?.toLowerCase().includes(queryLower)
  const matchInBody = metadata.body?.toLowerCase().includes(queryLower)
  const matchInSummary = metadata.summary?.toLowerCase().includes(queryLower)
  const matchInTranscript = metadata.transcript
    ?.toLowerCase()
    .includes(queryLower)

  return Boolean(
    matchInSubject || matchInBody || matchInSummary || matchInTranscript,
  )
}

/**
 * Determines what action should be taken when deeplinking to a search result
 *
 * @param eventType - The thread event type
 * @param query - The search query string
 * @param metadata - Event metadata containing searchable fields
 * @returns The type of action to take
 */
export function getDeeplinkActionType(
  eventType: string,
  query: string,
  metadata: SearchMatchMetadata,
): DeeplinkActionType {
  // EMAIL_MESSAGE_RECEIVED: If match is only in quotedText (not primary fields),
  // scroll to the event AND open email drawer to show quoted context
  if (eventType === 'EMAIL_MESSAGE_RECEIVED') {
    const matchInPrimary = isMatchInPrimaryFields(query, metadata)
    if (!matchInPrimary) {
      return DeeplinkActionType.SCROLL_AND_DRAWER
    }
  }

  // Default: just scroll to the event
  return DeeplinkActionType.SCROLL
}
